import { createRouter, createWebHashHistory } from 'vue-router';
import { useSessionStore } from '@/stores/session';

/**
 * Routing for the FRVB Referee Nomination System.
 *
 * - admins land on /admin (Nominate is the default tab)
 * - referees land on /referee (their dashboard + scoresheet flow)
 * - /confirm/:token is the public scoresheet-via-link page
 *
 * Hash-based routing keeps the SPA portable behind any nginx config.
 */
export const router = createRouter({
    history: createWebHashHistory(import.meta.env.BASE_URL),
    routes: [
        { path: '/login',           name: 'login',     component: () => import('./views/LoginView.vue'),
                                    meta: { public: true } },
        // Bare / always sends to the user's natural landing.
        { path: '/',                redirect: () => {
            const session = useSessionStore();
            return session.defaultRoute();
        } },
        { path: '/new',             name: 'new-match', component: () => import('./views/NewMatchView.vue') },
        { path: '/admin',           name: 'admin',     component: () => import('./views/AdminView.vue'),
                                    children: [
            // The admin lands on a hub showing the four sections —
            // no auto-redirect into Designations. They pick where to
            // go; Designations is the visually loudest tile.
            { path: '',             component: () => import('./views/admin/AdminHome.vue') },
            // Designations is the single source of truth for seasons,
            // competitions, teams and matches — the QuickDesignatePanel
            // upserts all four on save. The dedicated Matches /
            // Seasons / Competitions / Teams tabs were removed once
            // that path covered every workflow.
            { path: 'nominations',  component: () => import('./views/admin/NominationsTab.vue') },
            // Referees is a two-branch section: Career (the designation
            // dashboard) and Management (the Refereeing Commission
            // roster). The shell view renders the sub-tab nav + the
            // active child; the top-nav popover provides the same
            // choice without leaving the current page.
            { path: 'referees',     component: () => import('./views/admin/RefereesView.vue'),
                                    children: [
                { path: '',           redirect: '/admin/referees/career' },
                { path: 'career',     component: () => import('./views/admin/RefereesTab.vue') },
                { path: 'management', component: () => import('./views/admin/RefereesManagementTab.vue') }
            ]},
            { path: 'users',        component: () => import('./views/admin/UsersTab.vue') },
            { path: 'settings',     component: () => import('./views/admin/SettingsTab.vue') }
        ]},
        // Restrict :id to the 8-char hex ids the system mints so the
        // path can't accidentally collide with the static
        // /admin/referees/career and /admin/referees/management
        // sub-tabs declared above.
        { path: '/admin/referees/:id([0-9a-f]{8})', component: () => import('./views/admin/RefereeProfileView.vue'), props: true },
        // Referee-side dashboard + scoresheet flow.
        { path: '/referee',         name: 'referee',   component: () => import('./views/RefereeDashboardView.vue') },
        { path: '/scoresheet/:nominationId',
                                    name: 'scoresheet',
                                    component: () => import('./views/ScoresheetSubmitView.vue'),
                                    props: true },
        { path: '/confirm/:token',  name: 'confirm',
                                    component: () => import('./views/ScoresheetSubmitView.vue'),
                                    props: true,
                                    meta: { public: true } },
        { path: '/:catchAll(.*)',   redirect: '/' }
    ]
});

// Auth guard — block protected routes until the session has been
// restored from the cookie, then enforce role-based access.
router.beforeEach(async (to) => {
    const session = useSessionStore();
    if (!session.ready) {
        await session.restore();
    }
    if (to.meta?.public) return true;
    if (!session.isAuthed) {
        return { name: 'login', query: { redirect: to.fullPath } };
    }
    if (!session.canAccess(to.path)) {
        return session.defaultRoute();
    }
    return true;
});
